import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import en from "./locales/en";
import it from "./locales/it";
import ar from "./locales/ar";
import ru from "./locales/ru";
import ja from "./locales/ja";

export const LOCALES = [
  { code: "en", label: "EN", name: "English", dir: "ltr" },
  { code: "it", label: "IT", name: "Italiano", dir: "ltr" },
  { code: "ar", label: "AR", name: "العربية", dir: "rtl" },
  { code: "ru", label: "RU", name: "Русский", dir: "ltr" },
  { code: "ja", label: "JA", name: "日本語", dir: "ltr" },
];

const DICTS = { en, it, ar, ru, ja };
const STORAGE_KEY = "gd_locale";

function detectInitial() {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage?.getItem(STORAGE_KEY);
  if (stored && DICTS[stored]) return stored;
  const browser = (navigator.language || "en").slice(0, 2).toLowerCase();
  return DICTS[browser] ? browser : "en";
}

/** Resolve dotted path like "hero.headline1" against the dictionary. */
function resolve(dict, key) {
  return key
    .split(".")
    .reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), dict);
}

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const [locale, setLocale] = useState(detectInitial);

  useEffect(() => {
    const meta = LOCALES.find((l) => l.code === locale) || LOCALES[0];
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", meta.code);
      document.documentElement.setAttribute("dir", meta.dir);
    }
    try {
      window.localStorage?.setItem(STORAGE_KEY, locale);
    } catch (err) {
      // Storage quota exceeded or disabled — non-fatal.
      if (process.env.NODE_ENV === "development") {
        console.debug("[locale] could not persist locale:", err);
      }
    }
  }, [locale]);

  const t = useCallback(
    (key, vars) => {
      const dict = DICTS[locale] || DICTS.en;
      let value = resolve(dict, key);
      if (value === undefined) value = resolve(DICTS.en, key);
      if (value === undefined) return key;
      if (typeof value === "string" && vars) {
        return Object.entries(vars).reduce(
          (acc, [k, v]) => acc.replace(new RegExp(`\\{${k}\\}`, "g"), String(v)),
          value
        );
      }
      return value;
    },
    [locale]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
      isRtl: (LOCALES.find((l) => l.code === locale) || LOCALES[0]).dir === "rtl",
    }),
    [locale, t]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export const useLocale = () => {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
};
